/**
 * @module app.shared
 */

import { autoinject } from "aurelia-framework";
import { Router } from 'aurelia-router';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { Api, apiCall } from 'app/shared/lib/api/api';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import { IWindowMessage } from 'app/shared/lib/essette-site';
import { AccountContext } from 'app/core/lib/account-context';
import { MenuRefreshEvent, TitleChangeEvent } from 'app/shared/lib/events';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { SyncService } from 'app/shared/services/sync-service';

const TRANSITIONAL_DISABLED_ERROR = `Transitional has not been enabled. If this is unexpected, please set essetteTransitional.enabled to true in the environment-config.json.`;

@autoinject()
export class TransitionalManager {


    ////****************** Fields and Properties **************************************////
    private baseUrl: string | null = null;
    private endPointUrl: string | null = null;
    public route: string = 'core/transitional';
    public url: string | null = null;
    public origins: ReadonlyArray<string> | null;
    private subscriptions: Subscription[] = [];
    private listening: boolean = false;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        protected readonly api: Api,
        private readonly env: EnvironmentService,
        private readonly accountContext: AccountContext,
        private readonly router: Router,
        private readonly events: EventAggregator,
        private readonly logger: AppLogger,
        private readonly syncService: SyncService) {

        this.baseUrl = this.env.config.essetteTransitional.baseUrl;
        this.origins = this.env.config.essetteTransitional.origins;
        this.endPointUrl = this.baseUrl + 'Transitional.ashx';
        this.url = this.baseUrl + 'SSO.aspx';

        this.subscriptions.push(
            this.events.subscribe(SessionChangedEvent,
                (e: SessionChangedEvent) => {
                    if(e.hasUserContextChanged) {
                        // if user context has changed (tenant, context) due to logIn/logOut/checkIn/etc.
                        this.logOut();
                    }
                })
        );
    }

    ////****************** Functions ***********************************************////
    private getDefaultWindow(): Window | null {
        const w = open('', 'Popup_Transitional', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        return w;
    }

    public checkOrigin(origin: string): boolean {
        if(this.origins) {
            return this.origins.includes(origin);
        } else {
            return false;
        }
    }

    public post(queryString: string): Promise<any> {

        return new Promise<any>((resolve, reject) => {

            const request = new XMLHttpRequest();
            request.onload = () => {
                if(request.status >= 200 || request.status < 300) {
                    try {
                        resolve(JSON.parse(request.response));
                    } catch(error) {
                        throw new Error(`Error parsing response from transitional: ${error.toString()}`);
                    }
                }
                else {
                    const result = JSON.parse(request.response);
                    reject(new Error(request.statusText));
                }
            };
            request.onerror = () => {
                reject(new Error(`XMLHttpRequest Error: ${request.statusText}`));
            };
            request.open('POST', this.endPointUrl + queryString, true);
            request.withCredentials = true;
            request.setRequestHeader('Content-Type', 'application/json');
            request.send();
        });

    }

    public listenForPostMessages() {
        if(!this.listening) {
            window.addEventListener(
                'message',
                this.handlePostMessage
            );
            this.listening = true;
        }
    }

    public handlePostMessage = (event: MessageEvent) => {

        if(this.env.config.debug && event?.origin) { console.log(`Transitional message received from ${event.origin}`); }

        if(!this.checkOrigin(event.origin)) {
            this.logger.error(`Transitional message event origin was not allowed: ${event.origin}`);
            return;
        }

        const message: IWindowMessage = event.data ?? null;
        if(!message) {
            this.logger.error(`Transitional message event did not contain data`);
            return;
        }

        if(this.env.config.debug) { console.log(message); }

        if(message.action === 'ACTIVITY') {
            // keep application unlocked
            this.syncService.update(true);
            return;
        }
        if(message.action === 'REFRESH_MENU') {
            //refresh menu and clear account context
            this.accountContext.clear();
            this.events.publish(new MenuRefreshEvent);
        }
        if(message.routeCode && message.routeURL) {
            this.syncService.update(true); // navigating is activity
            this.navigate(message.routeURL, message.queryString, message.type);
        }
        else if(message.type === 'primary' && message.title) {
            // update title, but only if from a 'primary' window (not 'window' or 'popup')
            // trigger TitleChangeEvent to make sure to only update the title on the page and not the route current instructions navModel title
            this.events.publish(new TitleChangeEvent(['Transitional', message.title]));
        }
    }

    public navigate(routeTemplate: string, queryString?: string, type?: string) {
        this.logger.debug(`routeTemplate: ${routeTemplate}; queryString: ${queryString}`);

        //translate query string values to routes when needed
        if(queryString) {
            const query = this.parseQuery(queryString);
            if(query.CarePlanID && routeTemplate.includes(':caseId')) {
                routeTemplate = routeTemplate.replace(':caseId', query.CarePlanID);
            } else if(query.AuthID && routeTemplate.includes(':authId')) {
                routeTemplate = routeTemplate.replace(':authId', query.AuthID);
            }

            //Build queue parameters
            if(query.QueueTypeCode) {
                routeTemplate += `?queueTypeCode=${query.QueueTypeCode}`;
                if(query.QueueID) { routeTemplate += `&queueId=${query.QueueID}`; }
                if(query.QueueKeyValue) { routeTemplate += `&queueTargetId=${query.QueueKeyValue}`; }
                if(query.QueueAction) { routeTemplate += `&queueAction=${query.QueueAction}`; }
                if(query.AuthClassID) { routeTemplate += `&authClassID=${query.AuthClassID}`; }
                if(query.SourceCode) { routeTemplate += `&sourceCode=${query.SourceCode}`; }
            }
        }
        const url = this.env.resolveUrl(`~/#/${routeTemplate}`);
        if(type === 'popup') {
            // try to open in new window, but it probably won't work [Matthew J]
            const w = window.open() || window;
            w.location.assign(url);
        }
        else {
            window.location.assign(url);
        }
    }

    private parseQuery(search: any): any {
        if(!search) {
            return {};
        }
        const query: any = {};
        const pairs = (search[0] === '?' ? search.substr(1) : search).split('&');
        for(let pair of pairs) {
            const split = pair.split('=');
            query[decodeURIComponent(split[0])] = decodeURIComponent(split.length > 1 ? split[1] || '' : '');
        }
        return query;
    }

    public openTransitional(w: Window | null, pageId: string, transitionalIds: string[] = [], moduleCode: string = ''): void {
        if(!this.env.config.essetteTransitional.enabled) {
            return this.notifyTransitionalDisabled();
        }

        // Default window?
        if(!w) {
            w = this.getDefaultWindow();
        }

        const promise: Promise<IApiActionResults> = this.getSAMLResponse();
        promise.then((response) => {
            if(w) {
                w.document.write('<!DOCTYPE html><html><body style="background-color: white">Opening Transitional Please Wait...</body></html>');
                const f: HTMLFormElement = w.document.createElement('form');
                const i: HTMLInputElement = w.document.createElement('input');
                f.method = 'post';
                i.name = 'SAMLResponse';
                i.hidden = true;
                i.value = btoa(response.resultText || '');
                f.appendChild(i);
                let action = window.location.protocol + this.url + `?TargetPageID=${pageId}`;
                action += `&TenantID=${this.env.tenant.id}`;
                action += `&LocationID=${this.env.location.id}`;
                if(transitionalIds.length > 0) { action += `&TransitionalIDs=${this.buildTransitionalIdsValue(transitionalIds)}`; }
                if(moduleCode && moduleCode.length > 0) { action += `&ModuleCode=${moduleCode}` }
                action += `&Origin=${window.location.origin}`;
                f.action = action;
                w.document.getElementsByTagName('body')[0].appendChild(f);
                f.submit();
            }
        });
    }

    private buildTransitionalIdsValue(transitionalIds: string[] = []): string {
        const ids = transitionalIds.reduce<string>((val, id, idx, array) => {
            const isLastItem = idx === array.length - 1;
            const encodedId = encodeURIComponent(id);  // encode the individual querystring parameter value
            return val += `${encodedId}${!isLastItem ? '|' : ''}`;
        }, '');
        return encodeURIComponent(ids);  // encode final querystring
    }

    public routeToTransitional(pageId: string, transitionalIds: string[] = []): void {
        if(!this.env.config.essetteTransitional.enabled) {
            return this.notifyTransitionalDisabled();
        }
        this.router.navigateToRoute('core.transitional', { pageId: pageId, transitionalIds: transitionalIds.join('|') });
    }

    public notifyTransitionalDisabled(): void {
        return this.logger.warn(TRANSITIONAL_DISABLED_ERROR);
    }

    public async logOut() {
        await this.post('?action=logout');
    }

    // Member 360 Popup
    public openMemberPopup(memberId: string) {
        const w = this.getDefaultWindow();
        this.openTransitional(w, '9007', [memberId], '');
    }

    // Provider Info Popup
    public openProviderPopup(providerId: string) {
        const w = this.getDefaultWindow();
        this.openTransitional(w, '9003', [providerId], '');
    }

    // CS Issue Info Popup
    public openCSIssuePopup(issueId: string | null | undefined) {
        if(!issueId) {
            alert("Issue Id missing or invalid.");
            return;
        }

        const w = this.getDefaultWindow();
        this.openTransitional(w, '2007', [issueId], '');
    }

    // CS Issues Call Summary Info Popup
    public openCSCallSummaryPopup(contactLogId: string | null | undefined) {
        if(!contactLogId) {
            alert("Contact Log Id missing or invalid.");
            return;
        }

        const w = this.getDefaultWindow();
        this.openTransitional(w, '2006', [contactLogId], '');
    }


    // AG Case Info Popup
    public openAGCasePopup(caseId: string | null | undefined) {
        if(!caseId) {
            alert("Case Id missing or invalid.");
            return;
        }

        const w = this.getDefaultWindow();
        this.openTransitional(w, '6004', [caseId], '');
    }

    // PM Candidate Info Popup
    public openPMCandidatePopup(candidateId: string | null | undefined) {
        if(!candidateId) {
            alert("Candidate Id missing or invalid.");
            return;
        }

        const w = this.getDefaultWindow();
        this.openTransitional(w, '14007', [candidateId], '');
    }

    // HL7 Message Detail Popup
    public openHL7MessageDetailPopup(messageId: string | null | undefined) {
        if(!messageId) {
            alert("Message Id missing or invalid.");
            return;
        }

        const w = this.getDefaultWindow();
        this.openTransitional(w, '23002', [messageId], '');
    }

    @apiCall
    private async getSAMLResponse(): Promise<IApiActionResults> {
        const action = this.api.action('singlesignon', 'account', { code: 'Transitional' });
        return await this.api.post(action);
    }

}
